<template>
  <base-section
    id="solution-intro"
    space="50"
  >
    <v-container
      class="py-0"
      style="max-width: 1160px"
    >
      <base-breadcrumb :items="breadcrumbItems" />
    </v-container>

    <base-section-heading
      class="seo-only-heading"
      color="#142d7a"
      :title="solutionHeadingTitle"
      :subtitle="solutionHeadingSubtitle"
      :mobile-size="'text-h5'"
      :size="'text-h4'"
      :space="isMobile ? 2 : 12"
      title-tag="h1"
      :is-uppercase="false"
    />

    <v-container class="pa-0 custom-container">
      <v-row
        v-if="loading"
        class="ma-0"
      >
        <v-col
          cols="12"
          class="text-center py-12"
        >
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
          />
          <p class="mt-4">
            {{ $t('common.loading') }}
          </p>
        </v-col>
      </v-row>
      <v-row
        v-else
        class="ma-0"
      >
        <v-col
          cols="12"
          class="pa-0"
        >
          <div
            v-if="solutionSummary"
            class="product-summary"
            v-html="solutionSummary"
          />
          <div
            v-else
            class="text-center py-12"
          >
            {{ $t('solutions.noData') }}
          </div>
        </v-col>
      </v-row>
    </v-container>
  </base-section>
</template>

<script>
  import { SEO_LANGS } from '@/utils/seo'
  import { sanitizeHtml } from '@/utils/sanitize'

  export default {
    name: 'SolutionIntro',

    data: () => ({
      solution: null,
      solutionSummary: '',
      solutionTitle: '',
      solutionDescription: '',
      loading: false,
    }),

    metaInfo() {
      const title = this.solutionHeadingTitle
      const description = this.solutionHeadingSubtitle || this.$t('solutions.subtitle')
      const metaInfo = {
        title,
        meta: [
          { vmid: 'description', name: 'description', content: description },
          { vmid: 'keywords', name: 'keywords', content: [title, 'Yooxplore', this.$t('solutions.title')].filter(Boolean).join(', ') },
        ],
      }

      if (this.solutionJsonLd) {
        metaInfo.script = [
          {
            vmid: 'solution-jsonld',
            type: 'application/ld+json',
            json: this.solutionJsonLd,
          },
        ]
      }

      return metaInfo
    },

    computed: {
      siteUrl() {
        return (process.env.VUE_APP_SITE_URL || 'https://yooxplore.com').replace(/\/$/, '')
      },
      solutionHeadingTitle() {
        return this.solutionTitle || this.solution?.name || this.solution?.title || this.$t('solutions.title')
      },
      solutionHeadingSubtitle() {
        return this.solutionDescription || this.solution?.desc || this.$t('solutions.subtitle')
      },
      canonicalPath() {
        return this.solution?.slug ? `/${this.$i18n.locale}/solutions/${this.solution.slug}` : this.$route.path
      },
      solutionJsonLd() {
        if (!this.solution) return null
        const image = this.absoluteUrl(this.solution.coverImage || this.solution.imageUrl || '')
        const json = {
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: this.solutionHeadingTitle,
          description: this.solutionHeadingSubtitle,
          url: `${this.siteUrl}${this.canonicalPath}`,
          brand: {
            '@type': 'Brand',
            name: 'Yooxplore',
          },
        }
        if (image) json.image = image
        if (this.solution.category) json.category = this.solution.category
        return json
      },
      isMobile() {
        return this.$vuetify.breakpoint.width <= 768
      },
      breadcrumbItems() {
        const lang = this.$i18n.locale
        const items = [
          { text: this.$t('nav.home'), to: `/${lang}` },
          { text: this.$t('nav.solutions'), to: `/${lang}/solutions` },
        ]
        if (this.solutionTitle) {
          items.push({ text: this.solutionTitle })
        }
        return items
      },
    },

    watch: {
      '$route.params.itemId': {
        handler(newId) {
          this.loadSolutionSummary(newId)
        },
        immediate: true,
      },
      '$i18n.locale': {
        handler() {
          if (this.$route.params.itemId) {
            this.loadSolutionSummary(this.$route.params.itemId)
          }
        },
      },
    },

    methods: {
      getSeoCanonicalPath() {
        return this.canonicalPath
      },
      getSeoAlternatePaths() {
        if (!this.solution?.slug) return null
        return SEO_LANGS.reduce((acc, lang) => {
          acc[lang] = `/${lang}/solutions/${this.solution.slug}`
          return acc
        }, {})
      },
      stripHtml(value) {
        if (!value) return ''
        return String(value)
          .replace(/<script[\s\S]*?<\/script>/gi, ' ')
          .replace(/<style[\s\S]*?<\/style>/gi, ' ')
          .replace(/<[^>]+>/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
      },
      truncate(value, maxLength = 160) {
        if (!value) return ''
        const text = String(value).trim()
        return text.length > maxLength ? `${text.slice(0, maxLength - 1).trim()}...` : text
      },
      absoluteUrl(value) {
        if (!value) return ''
        if (/^https?:\/\//i.test(value)) return value
        return `${this.siteUrl}/${String(value).replace(/^\/+/, '')}`
      },
      async loadSolutionSummary(itemId) {
        if (!itemId) {
          this.solution = null
          this.solutionSummary = ''
          this.solutionTitle = ''
          this.solutionDescription = ''
          return
        }

        // 优先按 slug 查询；纯数字 ID 则回退旧接口
        const isNumericId = /^\d+$/.test(String(itemId))
        const url = isNumericId
          ? `/public/solutions/${itemId}`
          : `/public/solutions/by-slug/${encodeURIComponent(itemId)}`

        this.loading = true
        try {
          const solution = await this.$axios({
            method: 'get',
            url,
            params: { lang: this.$i18n.locale },
          })

          if (!solution) {
            console.warn(`Solution with itemId ${itemId} not found`)
            this.solution = null
            this.solutionSummary = ''
            this.solutionTitle = ''
            this.solutionDescription = ''
            return
          }

          this.solution = solution
          this.solutionTitle = solution.title || solution.name || ''
          this.solutionDescription = this.truncate(this.stripHtml(solution.desc || solution.summary || ''))
          // 清洗富文本并为空 ALT 图片补充方案名（SEO）
          let html = sanitizeHtml(solution.summary || '')
          if (this.solutionTitle) {
            html = html.replace(/<img([^>]*)\balt=["'](\s*)["']/gi, `<img$1alt="${this.solutionTitle}"`)
          }
          this.solutionSummary = html
          if (solution.slug && this.$route.params.itemId !== solution.slug) {
            this.$router.replace({
              name: 'SolutionsItem',
              params: { lang: this.$i18n.locale, itemId: solution.slug },
              query: this.$route.query,
            })
          }
        } catch (error) {
          console.error('加载方案详情失败:', error)
          this.solution = null
          this.solutionSummary = ''
          this.solutionTitle = ''
          this.solutionDescription = ''
        } finally {
          this.loading = false
        }
      },
    },
  }
</script>

<style lang="sass" scoped>
.custom-container
  max-width: none !important

/* 保留 H1 语义给搜索引擎和辅助技术，但不占用视觉版面 */
.seo-only-heading
  position: absolute !important
  width: 1px !important
  height: 1px !important
  padding: 0 !important
  margin: -1px !important
  overflow: hidden !important
  clip: rect(0, 0, 0, 0) !important
  clip-path: inset(50%) !important
  white-space: nowrap !important
  border: 0 !important

.product-summary
  font-size: 16px
  line-height: 1.8
  color: rgba(0, 0, 0, 0.78)

.product-summary :deep(p)
  margin: 0

.product-summary :deep(img)
  display: block
  width: 100%
  max-width: 100%
  height: auto

.product-summary :deep(video)
  max-width: 100%

</style>
